import { QueryClient, useQuery } from "@tanstack/react-query";
import { organizationCountries, organizationCountryCode } from "../countries";
import { createContext, useContext, useState } from "react";
import {
  Button,
  Field,
  FieldGroup,
  FieldError,
  FieldLabel,
  Input,
  SearchSelect,
  SearchSelectRow,
} from "@sfc/ui";
import {
  normalizeOrganizationId,
  supportsOrganizationLookup,
  registryNames,
} from "@sfc/organization-lookup/registries";
import type { OrganizationLookupResult } from "@sfc/organization-lookup";
import {
  isUsOrganizationCountry,
  organizationSettingsOf,
  changeOrganizationIdentity,
  organizationFieldIssues,
  formatOrganizationTaxId,
} from "../organization";
import type { OrganizationAnswer } from "../types";
import type { QuestionRenderProps } from "./render";

export type OrganizationLookupService = (
  input: {
    countryCode: string;
    taxId: string;
  },
  options?: { signal?: AbortSignal },
) => Promise<OrganizationLookupResult>;
const LookupContext = createContext<OrganizationLookupService | null>(null);
export const OrganizationLookupProvider = LookupContext.Provider;

export function OrganizationRender({
  question,
  value,
  onChange,
  labelId,
  invalid,
  validationMode,
}: QuestionRenderProps) {
  const lookup = useContext(LookupContext);
  const settings = organizationSettingsOf(question);
  const answer: OrganizationAnswer =
    value?.kind === "organization"
      ? value
      : {
          kind: "organization",
          country: "",
          taxId: "",
          name: "",
          evidenceUrl: "",
        };
  const hasCountry = !!answer.country.trim();
  const isUs = isUsOrganizationCountry(answer.country);
  const isAu = organizationCountryCode(answer.country) === "AU";
  const taxIdLabel = isUs
    ? "EIN"
    : isAu
      ? "Australian business number (ABN)"
      : "Tax ID";
  const taxIdHelp = isUs
    ? "Enter the organization’s nine-digit Employer Identification Number (EIN), such as 12-3456789."
    : isAu
      ? "Enter the organization’s eleven-digit Australian business number (ABN), such as 97 830 467 530."
      : "Enter the organization’s tax identification number as issued in the selected country.";
  const fieldIssues = invalid
    ? organizationFieldIssues(answer, validationMode)
    : {};
  const countryCode = organizationCountryCode(answer.country) ?? "";
  const taxId = normalizeOrganizationId(countryCode, answer.taxId);
  const identifierLabel = countryCode === "AU" ? "ABN" : "EIN";
  const taxIssues = [
    ...(fieldIssues.taxId ?? []),
    ...(fieldIssues.lookup ?? []),
  ];
  return (
    <FieldGroup
      role="group"
      aria-labelledby={labelId}
      aria-invalid={invalid || undefined}
    >
      <p className="body-sm text-muted-foreground">
        {settings.description}
        {settings.lookup && supportsOrganizationLookup(countryCode)
          ? " Automatic lookup covers U.S. nonprofits and Australian registered charities. A matching public record does not verify current tax-exempt status."
          : ""}
      </p>
      <Field data-invalid={!!fieldIssues.country || undefined}>
        <FieldLabel>Country</FieldLabel>
        <SearchSelect
          aria-label="Country"
          items={organizationCountries}
          selectedId={organizationCountryCode(answer.country) ?? null}
          placeholder={answer.country || "Select a country…"}
          searchPlaceholder="Search countries…"
          emptyMessage="No countries found."
          onSelect={(code) =>
            onChange(changeOrganizationIdentity(answer, "country", code))
          }
          match={(country, query) =>
            [country.id, country.name, ...country.aliases].some((text) =>
              text.toLowerCase().includes(query.trim().toLowerCase()),
            )
          }
          renderTriggerLabel={(country) => country.name}
          renderRow={(country, { selected }) => (
            <SearchSelectRow title={country.name} selected={selected} />
          )}
        />
        <FieldError
          errors={fieldIssues.country?.map((message) => ({ message }))}
        />
      </Field>
      {hasCountry ? (
        <>
          <Field data-invalid={taxIssues.length > 0 || undefined}>
            <FieldLabel htmlFor={`${labelId}-taxId`}>{taxIdLabel}</FieldLabel>
            <Input
              id={`${labelId}-taxId`}
              value={formatOrganizationTaxId(answer)}
              placeholder={
                isUs ? "12-3456789" : isAu ? "97 830 467 530" : "Tax ID"
              }
              aria-invalid={taxIssues.length > 0 || undefined}
              aria-describedby={`${labelId}-taxId-help${taxIssues.length ? ` ${labelId}-taxId-error` : ""}`}
              autoComplete="off"
              onChange={(event) =>
                onChange(
                  changeOrganizationIdentity(
                    answer,
                    "taxId",
                    event.target.value,
                  ),
                )
              }
            />
            <p
              id={`${labelId}-taxId-help`}
              className="body-sm text-muted-foreground"
            >
              {taxIdHelp}
            </p>
            <FieldError
              id={`${labelId}-taxId-error`}
              errors={taxIssues.map((message) => ({ message }))}
            />
          </Field>
        </>
      ) : null}
      {answer.lookup ? (
        <div className="flex flex-col gap-2">
          <p className="body-md font-medium">{answer.name}</p>
          <a
            className="body-sm underline"
            href={answer.evidenceUrl}
            target="_blank"
            rel="noreferrer"
          >
            View supporting record
          </a>
          <p className="body-sm text-muted-foreground">
            You confirmed this recipient from a{" "}
            {registryNames[answer.lookup.source]} lookup retrieved{" "}
            {new Date(answer.lookup.retrievedAt).toLocaleDateString()}.
            Tax-exempt status has not been verified. Clear the organization to
            choose a different recipient.
          </p>
        </div>
      ) : !answer.country.trim() ? (
        <p className="body-sm text-muted-foreground">
          Select a country to continue.
        </p>
      ) : settings.lookup &&
        supportsOrganizationLookup(countryCode) &&
        lookup ? (
        taxId || answer.name.trim() || answer.evidenceUrl.trim() ? (
          <Lookup
            key={`${settings.expectedClassification}-${countryCode}-${taxId}`}
            countryCode={countryCode}
            taxId={taxId ?? answer.taxId.trim()}
            identifierLabel={identifierLabel}
            expectedClassification={settings.expectedClassification}
            needsDetails={!!fieldIssues.name || !!fieldIssues.evidenceUrl}
            labelId={labelId}
            answer={answer}
            lookup={lookup}
            onChange={onChange}
            manualFields={
              <OrganizationManualFields
                answer={answer}
                onChange={onChange}
                labelId={labelId}
                fieldIssues={fieldIssues}
                evidence={settings.evidence}
              />
            }
          />
        ) : (
          <p className="body-sm text-muted-foreground">
            {countryCode === "AU"
              ? "Enter a complete eleven-digit ABN to look up the charity."
              : "Enter a complete nine-digit EIN to look up the organization."}
          </p>
        )
      ) : (
        <OrganizationManualFields
          answer={answer}
          onChange={onChange}
          labelId={labelId}
          fieldIssues={fieldIssues}
          evidence={settings.evidence}
        />
      )}
      {answer.lookup ? (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="self-start px-0 font-normal text-muted-foreground"
          onClick={() =>
            onChange(changeOrganizationIdentity(answer, "taxId", ""))
          }
        >
          Clear organization
        </Button>
      ) : null}
    </FieldGroup>
  );
}

function OrganizationManualFields({
  answer,
  onChange,
  labelId,
  fieldIssues,
  evidence,
}: {
  answer: OrganizationAnswer;
  onChange: QuestionRenderProps["onChange"];
  labelId: string;
  fieldIssues: ReturnType<typeof organizationFieldIssues>;
  evidence: string;
}) {
  return (
    <>
      {(
        [
          ["name", "Organization name", "Legal name of the organization"],
          ["evidenceUrl", "Supporting link", "https://…"],
        ] as const
      ).map(([key, label, placeholder]) => (
        <Field key={key} data-invalid={!!fieldIssues[key] || undefined}>
          <FieldLabel htmlFor={`${labelId}-${key}`}>{label}</FieldLabel>
          <Input
            id={`${labelId}-${key}`}
            value={answer[key]}
            placeholder={placeholder}
            type={key === "evidenceUrl" ? "url" : "text"}
            aria-invalid={!!fieldIssues[key] || undefined}
            aria-describedby={
              [
                key === "evidenceUrl" ? `${labelId}-${key}-help` : null,
                fieldIssues[key] ? `${labelId}-${key}-error` : null,
              ]
                .filter(Boolean)
                .join(" ") || undefined
            }
            onChange={(event) =>
              onChange({
                ...answer,
                [key]: event.target.value,
                lookup: undefined,
              })
            }
          />
          {key === "evidenceUrl" ? (
            <p
              id={`${labelId}-${key}-help`}
              className="body-sm text-muted-foreground"
            >
              {evidence}
            </p>
          ) : null}
          <FieldError
            id={`${labelId}-${key}-error`}
            errors={fieldIssues[key]?.map((message) => ({ message }))}
          />
        </Field>
      ))}
    </>
  );
}

/** A normalized identifier owns its lookup and reveals manual entry only after a failed lookup. */
function Lookup({
  countryCode,
  taxId,
  identifierLabel,
  answer,
  lookup,
  onChange,
  expectedClassification,
  manualFields,
  needsDetails,
  labelId,
}: {
  labelId: string;
  countryCode: string;
  taxId: string;
  identifierLabel: string;
  needsDetails: boolean;
  manualFields: React.ReactNode;
  expectedClassification: "501c3" | "501c4" | undefined;
  answer: OrganizationAnswer;
  lookup: OrganizationLookupService;
  onChange: QuestionRenderProps["onChange"];
}) {
  // An isolated client also lets the shared renderer work without an app provider.
  const [client] = useState(() => new QueryClient());
  // The identity key resets this choice on country/identifier/category changes.
  // Keep it stable during edits so fallback inputs never remount on a keystroke.
  const [savedManualAnswer] = useState(
    () => !!(answer.name.trim() || answer.evidenceUrl.trim()),
  );
  const {
    data,
    isFetching: pending,
    isError: error,
    refetch,
  } = useQuery(
    {
      queryKey: ["organization", countryCode, taxId],
      enabled: !savedManualAnswer,
      queryFn: async ({ signal }) => {
        // Consuming Query's signal cancels both this pause and stale requests on edits/unmount.
        await new Promise<void>((resolve, reject) => {
          const abort = () => {
            clearTimeout(timer);
            reject(signal.reason);
          };
          const timer = setTimeout(() => {
            signal.removeEventListener("abort", abort);
            resolve();
          }, 350);
          signal.addEventListener("abort", abort, { once: true });
          if (signal.aborted) abort();
        });
        signal.throwIfAborted();
        return lookup({ countryCode, taxId }, { signal });
      },
      retry: false,
      staleTime: Infinity,
      gcTime: 0,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
    client,
  );
  if (savedManualAnswer) return manualFields;
  const result = pending ? undefined : data;
  const unavailable = !pending && (error || result?.status === "unavailable");
  const hasManualDetails = !!(answer.name.trim() || answer.evidenceUrl.trim());
  return (
    <div className="flex flex-col gap-3">
      <div role="status" aria-live="polite">
        {pending ? (
          <>
            <p className="body-sm text-muted-foreground">
              Looking up {identifierLabel}…
            </p>
            {needsDetails ? (
              <FieldError>
                Wait for the lookup to finish before continuing.
              </FieldError>
            ) : null}
          </>
        ) : null}
        {result?.status === "found" ? (
          <Field
            data-invalid={needsDetails || undefined}
            className="rounded-md border border-border p-3 text-foreground data-[invalid=true]:border-destructive data-[invalid=true]:text-foreground"
          >
            <p className="body-sm font-medium">Not selected yet</p>
            <p className="body-md font-medium">
              Organization found: {result.organization.name}
            </p>
            <p className="body-sm">
              {[
                result.organization.address.street,
                result.organization.address.city,
                result.organization.address.state,
                result.organization.address.postalCode,
              ]
                .filter(Boolean)
                .join(", ")}
            </p>
            {result.source === "propublica" ? (
              <p className="body-sm">
                Reported classification:{" "}
                {result.organization.classification === "other"
                  ? `IRS subsection ${result.organization.subsectionCode}`
                  : result.organization.classification === "501c3"
                    ? "501(c)(3)"
                    : "501(c)(4)"}
              </p>
            ) : (
              <p className="body-sm">
                Listed in the ACNC register dataset. This is a periodically
                updated snapshot, not a live status check.
              </p>
            )}
            {result.source === "propublica" &&
            expectedClassification &&
            result.organization.classification !== expectedClassification ? (
              <p className="body-sm">
                This record’s classification differs from the category requested
                by this question. Check the EIN before confirming this
                recipient.
              </p>
            ) : null}
            <p className="body-sm text-muted-foreground">
              {registryNames[result.source]} record retrieved{" "}
              {new Date(result.retrievedAt).toLocaleDateString()}. Confirm this
              is the intended recipient by choosing “Use this organization”
              below. Entering an {identifierLabel} alone does not complete this
              question.
            </p>
            <a
              href={result.sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="body-sm underline"
            >
              View source record
            </a>
            <Button
              type="button"
              variant="outline"
              className="self-start"
              aria-describedby={
                needsDetails ? `${labelId}-confirmation-error` : undefined
              }
              onClick={() =>
                onChange({
                  ...answer,
                  name: result.organization.name,
                  taxId:
                    result.source === "propublica"
                      ? result.organization.ein
                      : result.organization.taxId,
                  evidenceUrl: result.sourceUrl,
                  lookup:
                    result.source === "propublica"
                      ? {
                          source: result.source,
                          ein: result.organization.ein,
                          retrievedAt: result.retrievedAt,
                        }
                      : {
                          source: result.source,
                          taxId: result.organization.taxId,
                          retrievedAt: result.retrievedAt,
                        },
                })
              }
            >
              Use this organization
            </Button>
            {needsDetails ? (
              <FieldError id={`${labelId}-confirmation-error`}>
                Confirm this organization to continue.
              </FieldError>
            ) : null}
          </Field>
        ) : result?.status === "not_found" ? (
          <p className="body-sm">
            No matching record was found. You can still enter the details and a
            supporting link below.
          </p>
        ) : unavailable ? (
          <div className="flex flex-col gap-2">
            <p className="body-sm">
              Lookup is unavailable.{" "}
              {hasManualDetails
                ? "Enter the details manually below."
                : "Retry the lookup or enter the details manually below."}
            </p>
            {!hasManualDetails ? (
              <Button
                type="button"
                variant="outline"
                className="self-start"
                onClick={() => void refetch()}
              >
                Retry lookup
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      {!pending &&
      (error ||
        result?.status === "not_found" ||
        result?.status === "unavailable" ||
        result?.status === "unsupported_country")
        ? manualFields
        : null}
    </div>
  );
}
